import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  TextField,
  InputAdornment,
  IconButton,
  Popover,
} from '@mui/material';
import { EmojiEvents as TrophyIcon, CalendarToday as CalendarIcon } from '@mui/icons-material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parse } from 'date-fns';
import { PROGRESSION_EVENT_TAGS } from './AthleteProgressionTab';
import { teamService } from '../../services/teamService';
import { userService } from '../../services/userService';
import { activityService } from '../../services/activityService';
import { avatarService } from '../../services/avatarService';
import { relayService } from '../../services/relayService';
import { DailyActivity, PersonalRecord } from '../../types';
import { formatMinutesAsRaceTime } from '../../utils/prTimeFormat';

const EVENT_TYPE_TO_MILES: Record<string, number> = {
  '400m': 400 / 1609.34,
  '800m': 800 / 1609.34,
  '1600m': 1600 / 1609.34,
  '3200m': 3200 / 1609.34,
  '3k': 3000 / 1609.34,
  '5K': 5000 / 1609.34,
  '10K': 10000 / 1609.34,
  'Half Marathon': 13.1,
  Marathon: 26.2,
};

function milesRoughlyMatchesEvent(miles: number, eventType: string): boolean {
  const expected = EVENT_TYPE_TO_MILES[eventType];
  if (expected == null || expected <= 0) return false;
  return Math.abs(miles - expected) < 0.25;
}

function activityIsRaceResultForEvent(a: DailyActivity, eventType: string): boolean {
  if (a.raceTime == null) return false;
  const tag = (a.raceEventType || '').trim();
  if (tag === eventType) return true;
  if (tag.toLowerCase() === eventType.toLowerCase()) return true;
  if (a.runSubType === 'Race Day' && milesRoughlyMatchesEvent(a.milesRun ?? 0, eventType)) return true;
  return false;
}

function prRecordDateYmd(pr: PersonalRecord): string | null {
  try {
    const d = (pr.date as any)?.toDate?.() ?? new Date((pr.date as any)?.seconds * 1000);
    if (Number.isNaN(d.getTime())) return null;
    return format(d, 'yyyy-MM-dd');
  } catch {
    return null;
  }
}

const LEADERS_TOP_N = 10;
const formatRaceTime = formatMinutesAsRaceTime;

export interface LeaderRow {
  rank: number;
  userId: string;
  displayName: string;
  timeMinutes: number;
  dateYmd: string;
  source: 'pr' | 'log' | 'both' | 'relay';
  verified: boolean;
  location?: string;
}

interface TeamLeadersTabProps {
  teamId: string;
  initialEvent: string;
  initialMode: 'alltime' | 'range';
  initialRange: { start: Date; end: Date };
  onPrefsChange: (prefs: {
    leadersEvent: string;
    leadersMode: 'alltime' | 'range';
    leadersDateStart?: string;
    leadersDateEnd?: string;
  }) => void;
}

type Agg = { time: number; dateYmd: string; fromPR: boolean; fromAct: boolean; verified: boolean; location?: string };

function pickFaster(a: Agg | null, b: Agg): Agg {
  if (!a) return b;
  if (b.time < a.time - 0.005) return b;
  if (Math.abs(a.time - b.time) < 0.03) {
    return {
      time: Math.min(a.time, b.time),
      dateYmd: a.dateYmd,
      fromPR: a.fromPR || b.fromPR,
      fromAct: a.fromAct || b.fromAct,
      verified: a.verified || b.verified,
      location: b.location || a.location,
    };
  }
  return a.time <= b.time ? a : b;
}

function isValidDate(d: Date): d is Date {
  return d instanceof Date && !isNaN(d.getTime());
}

/** Parse typed date string (e.g. 9/17/2025 or 09/17/2025). */
function parseDateInput(s: string): Date | null {
  const t = s.trim();
  if (!t) return null;
  const formats = ['M/d/yyyy', 'MM/dd/yyyy', 'M/dd/yyyy', 'MM/d/yyyy'];
  for (const f of formats) {
    try {
      const d = parse(t, f, new Date());
      if (!isNaN(d.getTime())) return d;
    } catch {
      /* try next */
    }
  }
  return null;
}

/** When activity log wins but date matches a verified PR, show verified (same meet). */
function markVerifiedIfPrSameDate(
  agg: Agg,
  prs: PersonalRecord[],
  eventTag: string
): void {
  if (agg.verified) return;
  for (const pr of prs) {
    if ((pr.eventType as string) !== eventTag || pr.isVerified !== true) continue;
    const d = prRecordDateYmd(pr);
    if (d && d === agg.dateYmd) {
      agg.verified = true;
      return;
    }
  }
}

const TeamLeadersTab: React.FC<TeamLeadersTabProps> = ({
  teamId,
  initialEvent,
  initialMode,
  initialRange,
  onPrefsChange,
}) => {
  const [eventTag, setEventTag] = useState(initialEvent);
  const [mode, setMode] = useState<'alltime' | 'range'>(initialMode);
  const [dateStart, setDateStart] = useState(() =>
    isValidDate(initialRange.start) ? initialRange.start : new Date()
  );
  const [dateEnd, setDateEnd] = useState(() =>
    isValidDate(initialRange.end) ? initialRange.end : new Date()
  );
  const [appliedDateStart, setAppliedDateStart] = useState(() =>
    isValidDate(initialRange.start) ? initialRange.start : new Date()
  );
  const [appliedDateEnd, setAppliedDateEnd] = useState(() =>
    isValidDate(initialRange.end) ? initialRange.end : new Date()
  );
  const [fromInput, setFromInput] = useState('');
  const [toInput, setToInput] = useState('');
  const [datePickerAnchor, setDatePickerAnchor] = useState<{ el: HTMLElement; which: 'from' | 'to' } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [rows, setRows] = useState<LeaderRow[]>([]);

  const startStr = isValidDate(dateStart) ? format(dateStart, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
  const endStr = isValidDate(dateEnd) ? format(dateEnd, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
  const appliedStartStr = isValidDate(appliedDateStart) ? format(appliedDateStart, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');
  const appliedEndStr = isValidDate(appliedDateEnd) ? format(appliedDateEnd, 'yyyy-MM-dd') : format(new Date(), 'yyyy-MM-dd');

  const persist = useCallback(
    (overrides: Partial<Parameters<TeamLeadersTabProps['onPrefsChange']>[0]>) => {
      const datesTouched =
        overrides.leadersDateStart !== undefined || overrides.leadersDateEnd !== undefined;
      onPrefsChange({
        leadersEvent: overrides.leadersEvent ?? eventTag,
        leadersMode: overrides.leadersMode ?? mode,
        ...(datesTouched
          ? {
              leadersDateStart: overrides.leadersDateStart ?? startStr,
              leadersDateEnd: overrides.leadersDateEnd ?? endStr,
            }
          : {}),
      });
    },
    [eventTag, mode, startStr, endStr, onPrefsChange]
  );

  const leadersRangeKey = isValidDate(initialRange.start) && isValidDate(initialRange.end)
    ? `${format(initialRange.start, 'yyyy-MM-dd')}_${format(initialRange.end, 'yyyy-MM-dd')}`
    : 'default';
  useEffect(() => setEventTag(initialEvent), [initialEvent]);
  useEffect(() => setMode(initialMode), [initialMode]);
  useEffect(() => {
    if (isValidDate(initialRange.start)) setDateStart(initialRange.start);
    if (isValidDate(initialRange.end)) setDateEnd(initialRange.end);
  }, [leadersRangeKey]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      if (eventTag === 'cross-country') {
        setRows([]);
        setLoading(false);
        return;
      }

      const isRelayEvent = ['4x800', '4x400', '4x100', 'DMR'].includes(eventTag);
      if (isRelayEvent) {
        const dateRange =
          mode === 'range'
            ? {
                start: appliedStartStr,
                end: appliedEndStr,
              }
            : undefined;

        const relays = await relayService.getTeamRelayResults(
          teamId,
          eventTag as any,
          dateRange,
          5000
        );

        relays.sort((a, b) => a.overallTimeMinutes - b.overallTimeMinutes || a.date.localeCompare(b.date));
        const top = relays.slice(0, LEADERS_TOP_N);

        const allLegAthleteIds = Array.from(
          new Set(top.flatMap((r) => (r.legAthleteIds as unknown as string[])))
        );
        const users = await Promise.all(
          allLegAthleteIds.map((id) => userService.getUserById(id).catch(() => null))
        );
        const byId = new Map(allLegAthleteIds.map((id, i) => [id, users[i]]));

        const leaders: LeaderRow[] = top.map((relay, i) => {
          const participants = (relay.legAthleteIds as unknown as string[]).map(
            (id) => byId.get(id)?.displayName || id
          );
          return {
            rank: i + 1,
            userId: relay.id,
            displayName: participants.join(', '),
            timeMinutes: relay.overallTimeMinutes,
            dateYmd: relay.date,
            source: 'relay',
            verified: relay.isVerified === true,
            location: relay.location,
          };
        });

        setRows(leaders);
        return;
      }

      const members = await teamService.getTeamMembers(teamId);
      const athleteMembers = members.filter((m) => m.role !== 'coach' && m.role !== 'headCoach');
      const athleteIds = new Set(athleteMembers.map((m) => m.athleteId));
      const bestByUser = new Map<string, Agg>();

      if (mode === 'alltime') {
        const teamPRs = await avatarService.getTeamPRs(teamId);
        for (const pr of teamPRs) {
          if (!athleteIds.has(pr.userId)) continue;
          if ((pr.eventType as string) !== eventTag) continue;
          const d = prRecordDateYmd(pr);
          if (!d) continue;
          const agg: Agg = {
            time: pr.time,
            dateYmd: d,
            fromPR: true,
            fromAct: false,
            verified: pr.isVerified === true,
            location: pr.location,
          };
          bestByUser.set(pr.userId, pickFaster(bestByUser.get(pr.userId) ?? null, agg));
        }

        const teamActs = await activityService.getTeamActivities(teamId, 12000);
        const athleteIdList = Array.from(athleteIds);
        const mergedPerUser = await Promise.all(
          athleteIdList.map((uid) =>
            activityService.mergeTeamActivitiesWithUserActivities(uid, teamActs)
          )
        );
        const mergedByUser = new Map(
          athleteIdList.map((uid, i) => [uid, mergedPerUser[i]] as const)
        );
        for (const uid of athleteIdList) {
          for (const a of mergedByUser.get(uid) ?? []) {
            if (!activityIsRaceResultForEvent(a, eventTag)) continue;
            if (a.raceTime == null) continue;
            const agg: Agg = {
              time: a.raceTime as number,
              dateYmd: a.date,
              fromPR: false,
              fromAct: true,
              verified: false,
            };
            const prev = bestByUser.get(uid) ?? null;
            bestByUser.set(uid, pickFaster(prev, agg));
          }
        }
        Array.from(bestByUser.entries()).forEach(([, agg]) => {
          markVerifiedIfPrSameDate(agg, teamPRs, eventTag);
        });
      } else {
        for (const member of athleteMembers) {
          const uid = member.athleteId;
          let best: Agg | null = null;
          const acts = await activityService.getUserActivitiesForProgression(
            uid,
            teamId,
            startStr,
            endStr,
            2000
          );
          for (const a of acts) {
            if (!activityIsRaceResultForEvent(a, eventTag)) continue;
            best = pickFaster(best, {
              time: a.raceTime as number,
              dateYmd: a.date,
              fromPR: false,
              fromAct: true,
              verified: false,
            });
          }
          const prs = await avatarService.getUserPersonalRecords(uid, teamId);
          const pr = prs.find((p) => (p.eventType as string) === eventTag);
          if (pr) {
            const d = prRecordDateYmd(pr);
            if (d && d >= appliedStartStr && d <= appliedEndStr) {
              best = pickFaster(best, {
                time: pr.time,
                dateYmd: d,
                fromPR: true,
                fromAct: false,
                verified: pr.isVerified === true,
                location: pr.location,
              });
            }
          }
          if (best) {
            markVerifiedIfPrSameDate(best, prs, eventTag);
            bestByUser.set(uid, best);
          }
        }
      }

      const entries = Array.from(bestByUser.entries());
      const users = await Promise.all(entries.map(([uid]) => userService.getUserById(uid)));
      const leaders: LeaderRow[] = entries.map(([userId, agg], i) => {
        const u = users[i];
        const source: LeaderRow['source'] =
          agg.fromPR && agg.fromAct ? 'both' : agg.fromPR ? 'pr' : 'log';
        return {
          rank: 0,
          userId,
          displayName: u?.displayName || u?.email || userId,
          timeMinutes: agg.time,
          dateYmd: agg.dateYmd,
          source,
          verified: agg.verified,
          location: agg.location,
        };
      });

      leaders.sort((a, b) => a.timeMinutes - b.timeMinutes || a.displayName.localeCompare(b.displayName));
      const top = leaders.slice(0, LEADERS_TOP_N);
      top.forEach((r, i) => {
        r.rank = i + 1;
      });
      setRows(top);
    } catch (e) {
      console.error(e);
      setError('Could not load leaders.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [teamId, eventTag, mode, appliedStartStr, appliedEndStr]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (isValidDate(dateStart)) setFromInput(format(dateStart, 'M/d/yyyy'));
    if (isValidDate(dateEnd)) setToInput(format(dateEnd, 'M/d/yyyy'));
  }, [dateStart, dateEnd]);

  const commitFromInput = () => {
    const d = parseDateInput(fromInput);
    if (d) setDateStart(d);
    else if (isValidDate(dateStart)) setFromInput(format(dateStart, 'M/d/yyyy'));
  };
  const commitToInput = () => {
    const d = parseDateInput(toInput);
    if (d) setDateEnd(d);
    else if (isValidDate(dateEnd)) setToInput(format(dateEnd, 'M/d/yyyy'));
  };

  const applyDateRange = () => {
    if (!isValidDate(dateStart) || !isValidDate(dateEnd)) return;
    setAppliedDateStart(dateStart);
    setAppliedDateEnd(dateEnd);
    persist({
      leadersDateStart: format(dateStart, 'yyyy-MM-dd'),
      leadersDateEnd: format(dateEnd, 'yyyy-MM-dd'),
    });
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: { xs: 1, md: 2 } }}>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrophyIcon /> Team leaders
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Fastest times by event. <strong>All-time</strong> uses current PRs plus every race log on the team.
          <strong> Time frame</strong> uses only performances (and PR dates) inside the range.
        </Typography>

        <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
          <Grid size={{ xs: 12, sm: 4 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="leaders-event-label">Event</InputLabel>
              <Select
                labelId="leaders-event-label"
                label="Event"
                value={eventTag}
                onChange={(e) => {
                  const v = e.target.value;
                  setEventTag(v);
                  persist({ leadersEvent: v });
                }}
              >
                {PROGRESSION_EVENT_TAGS.map((t) => (
                  <MenuItem key={t.value} value={t.value}>
                    {t.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 5 }}>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={mode}
              onChange={(_, v) => {
                if (v != null) {
                  setMode(v);
                  persist({ leadersMode: v });
                }
              }}
              fullWidth
            >
              <ToggleButton value="alltime">All-time</ToggleButton>
              <ToggleButton value="range">Time frame</ToggleButton>
            </ToggleButtonGroup>
          </Grid>
          {mode === 'range' && (
            <>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="From"
                  placeholder="M/D/YYYY or click calendar"
                  value={fromInput}
                  onChange={(e) => setFromInput(e.target.value)}
                  onBlur={commitFromInput}
                  onKeyDown={(e) => e.key === 'Enter' && commitFromInput()}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={(e) => setDatePickerAnchor({ el: e.currentTarget, which: 'from' })}
                          aria-label="Pick date"
                        >
                          <CalendarIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  size="small"
                  fullWidth
                  label="To"
                  placeholder="M/D/YYYY or click calendar"
                  value={toInput}
                  onChange={(e) => setToInput(e.target.value)}
                  onBlur={commitToInput}
                  onKeyDown={(e) => e.key === 'Enter' && commitToInput()}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={(e) => setDatePickerAnchor({ el: e.currentTarget, which: 'to' })}
                          aria-label="Pick date"
                        >
                          <CalendarIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Popover
                open={!!datePickerAnchor}
                onClose={() => setDatePickerAnchor(null)}
                anchorEl={datePickerAnchor?.el}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Box sx={{ p: 1 }}>
                  <DatePicker
                    value={datePickerAnchor?.which === 'from'
                      ? (isValidDate(dateStart) ? dateStart : null)
                      : (isValidDate(dateEnd) ? dateEnd : null)}
                    onChange={(d) => {
                      if (!d || !isValidDate(d)) return;
                      if (datePickerAnchor?.which === 'from') {
                        setDateStart(d);
                        setFromInput(format(d, 'M/d/yyyy'));
                      } else {
                        setDateEnd(d);
                        setToInput(format(d, 'M/d/yyyy'));
                      }
                      setDatePickerAnchor(null);
                    }}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </Box>
              </Popover>
              <Grid size={{ xs: 12, sm: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={applyDateRange}
                  disabled={loading || !isValidDate(dateStart) || !isValidDate(dateEnd)}
                >
                  Apply date range
                </Button>
              </Grid>
            </>
          )}
          <Grid size={{ xs: 12, sm: 2 }}>
            <Button variant="outlined" fullWidth onClick={() => load()} disabled={loading}>
              Refresh
            </Button>
          </Grid>
        </Grid>

        {eventTag === 'cross-country' && (
          <Alert severity="info">Pick a timed distance (e.g. 5K) for a leaderboard. Cross country uses varied courses.</Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : eventTag !== 'cross-country' && rows.length === 0 ? (
          <Alert severity="info">
            No results for this event{mode === 'range' ? ' in this time frame' : ''}. Try another event or range.
          </Alert>
        ) : eventTag !== 'cross-country' ? (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Athlete</TableCell>
                  <TableCell align="right">Time</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Source</TableCell>
                  {mode === 'alltime' && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.userId}>
                    <TableCell>{r.rank}</TableCell>
                    <TableCell>{r.displayName}</TableCell>
                    <TableCell align="right">
                      <Typography component="span" fontWeight={600} color="primary">
                        {formatRaceTime(r.timeMinutes)}
                      </Typography>
                    </TableCell>
                    <TableCell>{r.dateYmd}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={
                          r.source === 'relay'
                            ? 'Relay results'
                            : r.source === 'pr'
                              ? 'PR table'
                              : r.source === 'log'
                                ? 'Race log'
                                : 'PR + log'
                        }
                        variant="outlined"
                      />
                      {r.verified && (
                        <Chip size="small" label="Verified" color="success" sx={{ ml: 0.5 }} />
                      )}
                    </TableCell>
                    {mode === 'alltime' && (
                      <TableCell sx={{ maxWidth: 180 }}>{r.location || '—'}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : null}
      </Box>
    </LocalizationProvider>
  );
};

export default TeamLeadersTab;
